<template>
  <v-card class="ma-0">
    <v-toolbar color="primary" flat dark class="mb-4">
      <v-card-title>Edit Transaction</v-card-title>
    </v-toolbar>
    <v-card-text>
      <v-row class="mx-2">
        <v-col cols="12">
          <v-text-field label="Receipt No" hide-details="auto" dense outlined v-model="receiptNo"
            :error-messages="verifyErrors.receipt_no" readonly />
        </v-col>

        <v-col cols="12">
          <v-autocomplete label="Stock Code" :items="computedStockList" :search-input.sync="stockSearchInput"
            :loading="stockList.loading" item-value="stockCode" v-model="selectedStock" return-object
            hide-details="auto" dense outlined :disabled="isLocked">
            <template #selection="{ item }">
              <span>{{ item.supplierId }}-{{ item.stockCode }}</span>
              <span class="blue-grey--text mx-1 text-caption">{{ item.productName }}</span>
            </template>
            <template #item="{ item }">
              <div class="d-flex justify-space-between w-100">
                <div class="d-flex">
                  <v-icon v-if="item.promotion">mdi-information-variant</v-icon>
                  <v-icon v-if="item.wholeSale?.conditions?.length">mdi-package</v-icon>
                  <span class="blue--text mx-1">{{ item.supplierId }}-{{ item.stockCode }}</span>
                </div>
                <small>{{ item.productName }}</small>
              </div>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col v-if="selectedStock?.wholeSale?.conditions?.length">
          <whole-sale-info :conditions="selectedStock.wholeSale.conditions" :quantity="quantity" :retail="retail" />
        </v-col>

        <v-col v-if="!!selectedStock?.promotion">
          <promo-info :promo-item="promoItem" :supplier-product="selectedStock" :allow-promo="allowPromo"
            @update:allowPromo="allowPromo = $event" />
        </v-col>

        <v-col cols="12">
          <v-text-field hide-details="auto" dense outlined label="Senior ID" v-model="refNumber"
            :error-messages="verifyErrors.ref_number" />
        </v-col>

        <v-col cols="12">
          <v-combobox hide-details="auto" dense outlined v-model="consumer" :items="computedConsumers"
            :search-input.sync="searchConsumer" item-value="controlNumber" label="Consumer" no-filter return-object
            @keyup.enter="onConsumerComboboxEnter">
            <template #selection="{ item }">
              <small>({{ item.controlNumber }})</small> <span>{{ item.name }}</span>
            </template>
            <template #item="{ item }">
              <small>({{ item.controlNumber }})</small> <span>{{ item.name }}</span>
            </template>
          </v-combobox>
        </v-col>

        <v-col cols="12">
          <v-text-field hide-details="auto" dense outlined type="date" label="Transaction Date"
            v-model="dateTransaction" :error-messages="verifyErrors.date_transaction" />
        </v-col>

        <v-col cols="12">
          <v-text-field hide-details="auto" dense outlined label="SRP" prefix="₱" type="number" v-model.number="retail"
            :error-messages="verifyErrors.retail" />
        </v-col>

        <v-col cols="3">
          <v-text-field hide-details="auto" dense outlined label="Discount/Item" suffix="%" type="number" min="0"
            max="100" v-model.number="itemDiscountPercentage"
            :disabled="!!selectedStock?.promotion || !!selectedStock?.wholeSale?.length" />
        </v-col>

        <v-col cols="9">
          <v-text-field hide-details="auto" dense outlined label="Total Discounted Amnt" prefix="₱" type="number"
            v-model.number="discount" />
        </v-col>

        <v-col cols="12">
          <v-text-field hide-details="auto" dense outlined type="number" :rules="quantityRules"
            v-model.number="quantity" :suffix="unit">
            <template #label>
              <span>Quantity</span>
              <span class="mx-1" v-if="quantityLeft > 0">({{ quantityLeft }})</span>
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12">
          <v-textarea hide-details="auto" dense outlined label="Notes" auto-grow no-resize v-model="notes" rows="1" />
        </v-col>

        <v-col cols="12">
          <v-btn color="green" block @click="updateTransaction" :loading="verifying">
            Update Transaction
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { moneyMixin } from '@/views/dashboard/mixins/helper.mixins'
import { mapState, mapActions } from 'vuex'
import { authAxios } from '@/extra-functions/axios.extra'
import { ErrorPopup, SuccessPopup, LoadingPopup, useWatchEffect } from '@/lib/utils'
import PromotionUtil from '@/lib/promotion.util'
import WholeSaleUtil from '@/lib/whole-sale.util'
import DiscountUtil from '@/lib/discount.util'

const [watchEffect, clearWatchEffect] = useWatchEffect()

export default {
  name: 'editSaleInventories',
  components: {
    wholeSaleInfo: () => import('../WholeSaleInfo.vue'),
    promoInfo: () => import('../PromoInfo.vue'),
  },
  data() {
    return {
      ...inputVars(),
      ...consumerVars(),
      ...tableVars(),
      stockList: { list: [], loading: false },
      itemDiscountPercentage: null,
      isLocked: false,
      seniorDiscountRate: 0.2,
      storedStock: null,
      saleInventoryId: null,
    }
  },
  computed: {
    ...mapState({
      verifyErrors: state => state.saleInventory.verifyErrors,
      verifying: state => state.saleInventory.verifying,
      storeId: state => state.settings.storeId,
      authToken: state => state.auth.token,
    }),
    computedStockList() {
      if (!this.storedStock) return this.stockList.list
      return this.stockList.list.concat(this.storedStock)
    },
    computedConsumers() {
      if (!this.storedConsumer) return this.consumers
      return this.consumers.concat(this.storedConsumer)
    },
  },
  watch: {
    quantity() {
      this.$nextTick(() => this.applyPromoAndDiscounts())
    },
    retail() {
      this.$nextTick(() => this.applyPromoAndDiscounts())
    },
    refNumber() {
      this.$nextTick(() => this.applyPromoAndDiscounts())
    },
    selectedStock(sp) {
      this.allowPromo = !!sp?.promotion
      if (!sp) {
        this.stockId = null
        this.supplierId = null
        this.retail = null
        this.unit = null
        this.discount = 0
        this.itemDiscountPercentage = null
        return
      }
      this.stockId = sp.stockId
      this.supplierId = sp.supplierId
      this.retail = sp.retail
      this.unit = sp.unit

      // ensure a sensible default qty so tiers can trigger
      if (!this.quantity) this.quantity = 1
      this.$nextTick(() => this.applyPromoAndDiscounts())
    },
  },
  created() {
    this.fetchUnitsAll()
    this.fetchStocks()
    this.fetchConsumers()
  },
  beforeDestroy() {
    clearWatchEffect()
  },
  methods: {
    ...mapActions({
      fetchUnitsAll: 'unit/fetchAll',
      updateSaleInventory: 'saleInventory/update',
    }),
    fetchStocks() {
      watchEffect(async (onCleanup) => {
        try {
          this.stockList.loading = true
          const controller = new AbortController()
          onCleanup(() => {
            controller.abort()
          })
          const axios = authAxios()
          const { data } = await axios.get('/v2/stock-suppliers/dropdown', {
            params: { search: this.stockSearchInput },
            signal: controller.signal,
          })
          this.stockList.list = data.data
        } catch (e) {
          ErrorPopup.fire('Error', 'Failed to get stocks retrying in 3 secs')
          setTimeout(this.fetchStocks, 3000)
        } finally {
          this.stockList.loading = false
        }
      }, 'SaleInventoryV2Create_fetchStocks')
    },
    fetchConsumers() {
      watchEffect(async (callback) => {
        try {
          const controller = new AbortController()
          callback(() => {
            controller.abort()
          })
          this.searchingConsumer = true
          const axios = authAxios()
          const { data } = await axios.get('/v2/consumers/dropdown', {
            params: {
              search: this.searchConsumer,
            },
            signal: controller.signal,
          })
          this.consumers = data?.data || []
        } catch (e) {
          console.error(e.message)
          ErrorPopup.fire('Error', 'Something went wrong while retrieving consumer list retrying in 3 sec...')
          setTimeout(this.fetchConsumers, 3000)
        } finally {
          this.searchingConsumer = false
        }
      }, 'SaleInventoryV2Create_fetchConsumers')
    },
    init(saleInventoryId) {
      this.saleInventoryId = saleInventoryId
      watchEffect((onCleanUp) => {
        const controller = new AbortController()
        onCleanUp(() => controller.abort())
        this.fetchSaleData(saleInventoryId, controller)
      }, 'SaleInventoryV2Edit_fetchSaleData')
    },
    /**
     * Description
     * @param {number} saleInventoryId
     * @param {AbortController} [controller]
     */
    async fetchSaleData(saleInventoryId, controller) {
      try {
        LoadingPopup.fire('Loading', 'Fetching sale details...')
        const axios = authAxios()
        const { data: sale } = await axios.get(`/v2/sale-inventories/${saleInventoryId}/edit`, {
          signal: controller?.signal,
        })
        if (!sale) throw new Error('sale inventory is undefined')
        Object.assign(this, {
          receiptNo: sale.receiptNo,
          selectedStock: sale.stockSupplier,
          retail: sale.retail,
          quantity: sale.quantity,
          unit: sale.unit,
          discount: sale.discount,
          dateTransaction: sale.dateSold,
          notes: sale.notes,
          consumer: sale.consumer,
          refNumber: sale.refNumber,
          storedStock: sale.stockSupplier,
          storedConsumer: sale.consumer,
        })
        this.applyPromoAndDiscounts()
        LoadingPopup.close()
      } catch (e) {
        console.error(e)
        LoadingPopup.close()
        ErrorPopup.fire('Error', 'Failed to fetch sale data.')
      }
    },

    async updateTransaction() {
      try {
        LoadingPopup.fire('Updating', 'Saving changes...')
        const payload = {
          receipt_no: this.receiptNo,
          stockId: this.selectedStock.stockId,
          supplierId: this.selectedStock.supplierId,
          unit: this.unit,
          quantity: this.quantity,
          retail: this.retail,
          discount: this.discount,
          notes: this.notes,
          date_sold: this.dateTransaction,
          ref_number: this.refNumber,
          consumer_id: this.consumer?.id ?? null,
        }
        const axios = authAxios()
        await axios.put(`/v2/sale-inventories/${this.saleInventoryId}`, payload)
        SuccessPopup.fire('Success', 'Sale successfully updated')
        this.$emit('update-success')
      } catch (e) {
        LoadingPopup.close()
        ErrorPopup.fire('Error', 'Failed to update sale.')
      }
    },
    fetchSeniorDiscountRate() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => controller.abort())
          const axios = authAxios()
          const { data } = await axios.get('/v2/identity-card-types/senior-discount', { signal: controller.signal })
          this.seniorDiscountRate = data?.discountRate ?? 0.2
        } catch (e) {
          console.error(e)
          ErrorPopup.fire('Error', 'Something went wrong while fetching senior discount rate retrying in 3 sec')
          setTimeout(this.fetchSeniorDiscountRate, 3000)
        }
      }, 'SaleInventoryV2Create_fetchSeniorDiscountRate')
    },

    applyPromoAndDiscounts() {
      if (!this.selectedStock?.stockId) return
      const promoUtil = new PromotionUtil(this.selectedStock.stockId, this.selectedStock?.promotion?.promoItem)
      promoUtil.setQuantity(this.quantity)
      this.promoItem = promoUtil.getPromoItem()

      const wholeSaleUtil = new WholeSaleUtil(this.selectedStock?.wholeSale?.conditions || [])
      wholeSaleUtil.setQuantity(this.quantity)
      wholeSaleUtil.setRetail(this.retail)

      const seniorDiscountRate = Number(this.refNumber ? this.seniorDiscountRate : 0)
      const discountUtil = new DiscountUtil(promoUtil.getDiscount(), wholeSaleUtil.getDiscount(), seniorDiscountRate)
      discountUtil.setQuantity(this.quantity)
      discountUtil.setRetail(this.retail)
      this.itemDiscountPercentage = Math.round(discountUtil.getDiscountRate() * 1000) / 10
      this.discount = Math.round(discountUtil.getDiscount() * 100) / 100
    },
  },
  mixins: [moneyMixin],
}

function inputVars() {
  return {
    receiptNo: null,
    selectedStock: null,
    retail: null,
    quantity: null,
    discount: null,
    dateTransaction: null,
    notes: null,
    refNumber: null,
    allowPromo: false,
    unit: '',
  }
}
function consumerVars() {
  return {
    consumer: null,
    consumers: [],
    searchConsumer: null,
    tempConsumers: [],
    storedConsumer: null,
  }
}
function tableVars() {
  return { promoItem: null }
}
</script>
